import { Box, Button, Divider, FormControl, VStack } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Trans, withTranslation } from 'react-i18next';
import apiClient from '../../api';
import { verifyOtp } from '../../api/auth';
import * as ga from '../../google-analytics';
import { useCurrentColors } from '../../hooks/UseCurrentColors/UseCurrentColors';
import { useCheckForMobile } from '../../hooks/UseResponsive/UseResponsive';
import CanopyButton from '../CanopyButton/CanopyButton';
import CanopyInput from '../CanopyInput/CanopyInput';
import ErrorBox from '../ErrorBox/ErrorBox';
import { LoginFlow, LoginPageProps } from './LoginPage';
interface LoginFlowProps extends LoginPageProps {
    setLoginFlow: React.Dispatch<React.SetStateAction<LoginFlow>>;
    t: any;
    setLoginResponse: React.Dispatch<
        React.SetStateAction<Record<string, unknown>>
    >;
}
const Verification: React.FC<LoginFlowProps> = (props) => {
    const { setLoginFlow, api, t, setLoginResponse } = props;
    const currentColors = useCurrentColors();
    const isMobile = useCheckForMobile();
    const [verificationCode, setVerificationCode] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        ga.event({
            action: `2FA > Click on "Submit"`,
            user: apiClient.session,
        });
        event.preventDefault();
        setIsLoading(true);
        try {
            const response = await verifyOtp({
                code: verificationCode.toString(),
            });
            const { login_flow } = response;
            if (login_flow === 'logged_in') {
                setLoginFlow('logged_in');
            } else if (login_flow === 'reset_password') {
                setLoginResponse(response);
                setLoginFlow('reset_password');
            } else {
                // error
            }
            setLoginFlow('logged_in');
            setIsLoading(false);
        } catch (e: any) {
            if (e?.response?.body) {
                const { body } = e.response;
                setError(body?.error ? body?.error : 'Something went wrong');
            } else if (e?.response?.data) {
                const { data } = e.response;
                if (data.login_flow === 'reset_password') {
                    setLoginResponse({ ...data });
                    setLoginFlow('reset_password');
                } else {
                    setError(
                        data?.error ? data?.error : 'Something went wrong'
                    );
                }
            } else {
                setError('Something went wrong');
            }
            setIsLoading(false);
        }
    };

    return (
        <>
            <Box
                color={currentColors.contentText}
                mx="auto"
                width="300px"
                height="42px"
                fontSize="15px"
                mt="5px"
                textAlign="center">
                <Trans i18nKey="two-factor.enter-authenticator-pin-or-backup-code">
                    Enter the 6-digit code you see in the
                    <strong>Google Authenticator</strong> app or a Backup Code
                </Trans>
            </Box>
            <Divider
                borderColor={currentColors.brand['700']}
                width="420px"
                my="40px"
            />

            <form
                onSubmit={handleSubmit}
                style={{
                    width: isMobile ? '342px' : 'initial',
                    marginLeft: isMobile ? '7px' : 'initial',
                    marginRight: isMobile ? '7px' : 'initial',
                }}>
                <VStack spacing="26px">
                    {error && (
                        <ErrorBox
                            p="0"
                            m="0"
                            backgroundColor="inherit"
                            color={currentColors.errorText}
                            fontSize="14px"
                            fontWeight="700"
                            position="relative"
                            mb="20px"
                            error={error}></ErrorBox>
                    )}
                    <FormControl px={{ sm: '15px' }}>
                        <CanopyInput
                            maxLength={6}
                            width={[
                                '268px',
                                '315px',
                                '315px',
                                '420px',
                                '420px',
                            ]}
                            id="verificationCode"
                            placeholder={`${t(
                                'two-factor.enter-verification-code'
                            )}`}
                            label={`${t('two-factor.enter-verification-code')}`}
                            height="45px"
                            onChange={(
                                event: React.ChangeEvent<HTMLInputElement>
                            ) => {
                                setVerificationCode(event.target.value);
                                setError('');
                            }}></CanopyInput>
                    </FormControl>
                    <CanopyButton
                        mx={{ sm: '15px' }}
                        type="submit"
                        height="45px"
                        width={['268px', '315px', '315px', '420px', '420px']}
                        mt="42px"
                        isDisabled={!verificationCode}
                        isLoading={isLoading}>
                        {t('submit')}
                    </CanopyButton>
                </VStack>
            </form>
            <Button
                fontSize="sm"
                variant="link"
                width={['50%', '50%', '50%', '120px', '120px']}
                height="20px"
                mt="20px"
                color={currentColors.brand['300']}
                _hover={{ textDecoration: 'none' }}
                _focus={{ border: 'none' }}
                onClick={() => {
                    ga.event({
                        action: `2FA > Click on "Return to Login"`,
                        user: apiClient.session,
                    });
                    props.setLoginFlow('login');
                }}>
                {t('forgot-password.return-to-login')}
            </Button>
        </>
    );
};
Verification.propTypes = {
    t: PropTypes.func.isRequired,
};

export default withTranslation('translations')(Verification);
